/**
 * Base class for all AI tools
 * Provides common functionality and enforces consistent structure
 */
export class BaseTool {
  constructor({
    id,
    name,
    description,
    category,
    icon,
    runInstructions,
    definition,
    execute,
    type,
    options = {},
  }) {
    // Required properties
    if (
      !id ||
      !name ||
      !description ||
      !category ||
      !icon ||
      !definition ||
      !execute
    ) {
      throw new Error("Missing required tool configuration properties");
    }

    this.id = id;
    this.name = name;
    this.description = description;
    this.category = category;
    this.icon = icon;
    this.runInstructions = `* ${id} * ${runInstructions || description}`;
    this.definition = definition;
    this.execute = this.wrapExecute(execute);
    this.type = type || "function"; // Default to 'function' if not provided

    // Optional properties
    this.rateLimit = options.rateLimit;
    this.streamable = options.streamable || false;
    this.requiresAuth = options.requiresAuth || false;
    this.timeout = options.timeout || 60000; // Default 60 seconds
  }

  /**
   * Wraps the execute function with common functionality
   */
  wrapExecute(executeFn) {
    return async (...args) => {
      try {
        // Start execution tracking
        const startTime = Date.now();
        this.logExecution("start", { args });

        // Apply rate limiting if configured
        if (this.rateLimit) {
          await this.applyRateLimit();
        }

        // Execute with timeout
        const result = await Promise.race([
          executeFn(...args),
          new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error("Tool execution timeout")),
              this.timeout
            )
          ),
        ]);

        // Log successful completion
        this.logExecution("complete", {
          duration: Date.now() - startTime,
          result,
        });

        return result;
      } catch (error) {
        // Handle and log errors
        this.logExecution("error", { error });
        return this.handleError(error);
      }
    };
  }

  /**
   * Validates the tool configuration
   */
  validateConfig() {
    const requiredFields = [
      "id",
      "name",
      "description",
      "category",
      "icon",
      "definition",
      "execute",
    ];

    const missingFields = requiredFields.filter((field) => !this[field]);

    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(", ")}`);
    }

    // Validate ID format
    if (!/^[a-z_]+$/.test(this.id)) {
      throw new Error("Tool ID must be snake_case");
    }

    // Validate name format
    if (!/^[A-Z][A-Za-z\s&\-_:()]+$/.test(this.name)) {
      throw new Error("Tool name must be Title Case");
    }

    return true;
  }

  /**
   * Common error handling
   */
  handleError(error) {
    console.error(`Tool ${this.id} error:`, error);

    return {
      success: false,
      error: error.message,
      metadata: {
        toolId: this.id,
        timestamp: new Date().toISOString(),
        type: error.name,
        code: error.code,
      },
    };
  }

  /**
   * Rate limiting implementation
   */
  async applyRateLimit() {
    if (!this.rateLimit) return;

    const { maxRequests, perMinute } = this.rateLimit;
    // Implementation would go here - you could use your existing rateLimit utility
  }

  /**
   * Execution logging
   */
  logExecution(stage, data = {}) {
    const logEntry = {
      toolId: this.id,
      stage,
      timestamp: new Date().toISOString(),
      ...data,
    };

    // Could also send to monitoring/analytics service
  }
}

export default BaseTool;
